import type { Aspiration } from '../../../types/aspirations';
import type { Goal } from '../../../types/goals';
import type { List } from '../../../types/lists';

export const aspirations: Aspiration[] = [
  {
    id: 'aspiration_strong_relationships',
    title: 'Have strong relationships',
    description: 'Have strong relationships with my family and friends through consistent communication and quality time.',
    category: 'Personal',
    priority: 3,
    status: 'not_started',
    progress: 0,
    deadline: new Date('2024-12-31'),
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-11-18'),
    linkedGoals: [
      { id: 'goal_family_time', title: 'Schedule Regular Family Activities', completed: false, deadline: new Date('2025-03-31') },
      { id: 'goal_friend_connections', title: 'Strengthen Friendships', completed: false, deadline: new Date('2025-06-30') },
      { id: 'goal_communication_skills', title: 'Improve Communication Skills', completed: false, deadline: new Date('2025-09-30') }
    ]
  }
];

export const goals: Goal[] = [
  {
    id: 'goal_family_time',
    title: 'Schedule Regular Family Activities',
    description: 'Establish consistent quality time with family through regular activities and communication.',
    priority: 1,
    status: 'not_started',
    progress: 0,
    smartCriteria: {
      specific: 'Schedule weekly family dinner and monthly family activity',
      measurable: 'Complete 80% of scheduled family activities',
      achievable: 'Block time in calendar, involve family in planning',
      relevant: 'Strengthen family bonds and relationships',
      timeBound: 'Establish routine by March 2025'
    },
    deadline: new Date('2025-03-31'),
    startDate: new Date('2024-01-01'),
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-11-18'),
    aspirationId: 'aspiration_strong_relationships',
    lists: [
      {
        id: 'list_family_1',
        title: 'Weekly Family Dinners',
        description: 'Cook and eat together every Sunday',
        deadline: new Date('2025-01-31'),
        completed: false
      },
      {
        id: 'list_family_2',
        title: 'Monthly Adventures',
        description: 'Plan special monthly family activities',
        deadline: new Date('2025-03-31'),
        completed: false
      }
    ],
    successMetrics: ['Activity completion rate', 'Family satisfaction', 'Communication frequency'],
    resources: ['Calendar app', 'Activity planning resources', 'Family input'],
  },
  {
    id: 'goal_friend_connections',
    title: 'Strengthen Friendships',
    description: 'Maintain and deepen friendships through regular contact and shared experiences.',
    priority: 2,
    status: 'not_started',
    progress: 0,
    smartCriteria: {
      specific: 'Contact each close friend at least twice per month',
      measurable: 'Track communication frequency and friend meetups',
      achievable: 'Schedule regular check-ins and activities',
      relevant: 'Maintain important relationships in my life',
      timeBound: 'Establish consistent pattern by June 2025'
    },
    deadline: new Date('2025-06-30'),
    startDate: new Date('2024-09-01'),
    createdAt: new Date('2024-08-15'),
    updatedAt: new Date('2024-11-18'),
    aspirationId: 'aspiration_strong_relationships',
    lists: [
      {
        id: 'list_friends_1',
        title: 'Regular Check-ins',
        description: 'Schedule bi-weekly calls with close friends',
        deadline: new Date('2025-03-31'),
        completed: false
      },
      {
        id: 'list_friends_2',
        title: 'Friend Activities',
        description: 'Plan monthly activities with different friend groups',
        deadline: new Date('2025-06-30'),
        completed: false
      }
    ],
    successMetrics: ['Communication frequency', 'Friend meetup frequency', 'Relationship quality'],
    resources: ['Contact management app', 'Activity planning tools', 'Calendar reminders'],
  },
  {
    id: 'goal_portfolio',
    title: 'Build Creative Portfolio',
    description: 'Develop a comprehensive creative portfolio showcasing photography and design work.',
    priority: 3,
    status: 'not_started',
    progress: 0,
    smartCriteria: {
      specific: 'Create professional portfolio with 50 high-quality pieces across different categories',
      measurable: 'Complete portfolio website with 50 pieces, 10+ client testimonials',
      achievable: 'Leverage existing skills and dedicate 8 hours per week',
      relevant: 'Build reputation and potential income stream from creative work',
      timeBound: 'Complete portfolio by August 2025'
    },
    deadline: new Date('2025-08-31'),
    startDate: new Date('2024-11-01'),
    createdAt: new Date('2024-10-15'),
    updatedAt: new Date('2024-11-15'),
    aspirationId: 'aspiration_strong_relationships', // Creative expression supports relationships
    lists: [
      {
        id: 'list_photo_portfolio',
        title: 'Photography Portfolio',
        description: 'Building professional photography portfolio',
        deadline: new Date('2025-08-31'),
        completed: false
      }
    ],
    successMetrics: ['Portfolio completion rate', 'Client inquiries', 'Creative satisfaction'],
    resources: ['Camera equipment', 'Editing software', 'Website platform', 'Portfolio examples'],
  }
];

export const lists: List[] = [
  // Active Daily Routine (cloned from template)
  {
    id: 'list_today_routine',
    title: '🌅 Today\'s Morning Routine',
    description: 'Active morning routine for today',
    status: 'ready',
    priority: 1,
    goalId: 'goal_family_time',
    templateId: 'list_daily_routine',
    createdAt: new Date('2024-11-26'),
    updatedAt: new Date('2024-11-26'),
    estimatedHours: 3,
    actualHours: 1.5,
    items: [
      {
        id: 'item_today_wake',
        content: 'Wake up at 6:00 AM',
        priority: 1,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26T06:00:00')
      },
      {
        id: 'item_today_exercise',
        content: 'Strength training workout (45 min)',
        priority: 2,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26T06:45:00')
      },
      {
        id: 'item_today_protein',
        content: 'High-protein breakfast',
        priority: 3,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26')
      },
      {
        id: 'item_today_shower',
        content: 'Shower and get ready',
        priority: 4,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26')
      },
      {
        id: 'item_today_review',
        content: 'Review daily goals and priorities',
        priority: 5,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26')
      },
      {
        id: 'item_today_financial_check',
        content: 'Check investments and savings progress',
        priority: 6,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26')
      },
      {
        id: 'item_today_relationships',
        content: 'Text family/friends - stay connected',
        priority: 7,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26')
      },
      {
        id: 'item_today_work',
        content: 'Start focused work session',
        priority: 8,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26')
      }
    ]
  },

  // Photography Projects
  {
    id: 'list_photo_portfolio',
    title: '📸 Photography Portfolio',
    description: 'Building professional photography portfolio',
    status: 'ready',
    priority: 5,
    goalId: 'goal_portfolio',
    deadline: new Date('2025-08-31'),
    createdAt: new Date('2024-11-15'),
    updatedAt: new Date('2024-11-15'),
    estimatedHours: 60,
    items: [
      {
        id: 'item_photo_1',
        content: 'Landscape series - Golden Gate',
        description: 'Sunrise and sunset shots at different seasons',
        priority: 1,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 480,
        category: 'creative'
      },
      {
        id: 'item_photo_2',
        content: 'Street photography - SF neighborhoods',
        description: 'Candid shots in Mission, Chinatown, North Beach',
        priority: 2,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 600,
        category: 'creative'
      },
      {
        id: 'item_photo_3',
        content: 'Portrait series - Tech workers',
        description: 'Environmental portraits of developers',
        priority: 3,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 720,
        category: 'creative'
      },
      {
        id: 'item_photo_4',
        content: 'Night photography - City lights',
        description: 'Long exposure cityscapes',
        priority: 4,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 360,
        category: 'creative',
        difficulty: 'hard'
      },
      {
        id: 'item_photo_5',
        content: 'Nature macro series',
        description: 'Close-ups of flowers and insects',
        priority: 5,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 480,
        category: 'creative'
      },
      {
        id: 'item_photo_6',
        content: 'Edit and process RAW files',
        description: 'Post-processing in Lightroom',
        priority: 6,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 600,
        category: 'creative'
      },
      {
        id: 'item_photo_7',
        content: 'Create portfolio website',
        description: 'Design and build photography site',
        priority: 7,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 480,
        category: 'creative'
      },
      {
        id: 'item_photo_8',
        content: 'Print selection for exhibition',
        description: 'Choose 20 best photos for printing',
        priority: 8,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 180,
        category: 'creative'
      },
      {
        id: 'item_photo_9',
        content: 'Write artist statement',
        description: 'Describe artistic vision and process',
        priority: 9,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 120,
        category: 'creative'
      },
      {
        id: 'item_photo_10',
        content: 'Network with galleries',
        description: 'Contact local galleries for exhibition',
        priority: 10,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 240,
        category: 'creative'
      }
    ]
  },
  // Second list for family time goal
  {
    id: 'list_family_dinners',
    title: '🍽️ Weekly Family Dinners',
    description: 'Plan and prepare Sunday family dinners',
    status: 'ready',
    priority: 2,
    goalId: 'goal_family_time',
    deadline: new Date('2025-03-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-26'),
    estimatedHours: 4,
    items: [
      {
        id: 'item_dinner_1',
        content: 'Plan menu for the week',
        description: 'Choose family-friendly recipes',
        priority: 1,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 20,
        dueDate: new Date('2024-12-01'),
        category: 'personal'
      },
      {
        id: 'item_dinner_2',
        content: 'Shop for ingredients',
        description: 'Buy fresh ingredients for dinner',
        priority: 2,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 45,
        dueDate: new Date('2024-12-01'),
        category: 'personal'
      },
      {
        id: 'item_dinner_3',
        content: 'Cook dinner together',
        description: 'Involve family members in cooking',
        priority: 3,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 90,
        dueDate: new Date('2024-12-01'),
        category: 'personal'
      },
      {
        id: 'item_dinner_4',
        content: 'Family dinner conversation topics',
        description: 'Prepare meaningful questions',
        priority: 4,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 10,
        dueDate: new Date('2024-12-01'),
        category: 'personal'
      }
    ]
  },
  // Third list for family time goal
  {
    id: 'list_family_adventures',
    title: '🎯 Monthly Family Adventures',
    description: 'Plan special monthly activities',
    status: 'ready',
    priority: 3,
    goalId: 'goal_family_time',
    deadline: new Date('2025-03-31'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-26'),
    estimatedHours: 8,
    items: [
      {
        id: 'item_adv_1',
        content: 'Research local hiking trails',
        description: 'Find family-friendly hikes within 1hr',
        priority: 1,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-12-05'),
        category: 'personal'
      },
      {
        id: 'item_adv_2',
        content: 'Book museum or event tickets',
        description: 'Check for exhibits and shows',
        priority: 2,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 20,
        dueDate: new Date('2024-12-10'),
        category: 'personal'
      },
      {
        id: 'item_adv_3',
        content: 'Plan day trip itinerary',
        description: 'Map out schedule with breaks',
        priority: 3,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-12-15'),
        category: 'personal'
      }
    ]
  },
  // First list for friend connections goal
  {
    id: 'list_friend_checkins',
    title: '📞 Friend Check-ins',
    description: 'Regular calls and messages with close friends',
    status: 'ready',
    priority: 2,
    goalId: 'goal_friend_connections',
    deadline: new Date('2025-06-30'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-26'),
    estimatedHours: 6,
    items: [
      {
        id: 'item_checkin_1',
        content: 'Call Mike - catch up on life',
        description: 'Old college friend, monthly call',
        priority: 1,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 45,
        dueDate: new Date('2024-12-01'),
        category: 'personal'
      },
      {
        id: 'item_checkin_2',
        content: 'Text Sarah - birthday coming up',
        description: 'Plan surprise party',
        priority: 2,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 15,
        dueDate: new Date('2024-12-05'),
        category: 'personal'
      },
      {
        id: 'item_checkin_3',
        content: 'Video call with James',
        description: 'Friend moved to NYC, biweekly call',
        priority: 3,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 30,
        dueDate: new Date('2024-12-10'),
        category: 'personal'
      },
      {
        id: 'item_checkin_4',
        content: 'Send holiday cards to friends',
        description: 'Personalized notes',
        priority: 4,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 60,
        dueDate: new Date('2024-12-15'),
        category: 'personal'
      }
    ]
  },
  // Second list for friend connections goal
  {
    id: 'list_friend_activities',
    title: '🎮 Friend Activities',
    description: 'Monthly activities with different friend groups',
    status: 'ready',
    priority: 3,
    goalId: 'goal_friend_connections',
    deadline: new Date('2025-06-30'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-26'),
    estimatedHours: 10,
    items: [
      {
        id: 'item_act_1',
        content: 'Organize game night',
        description: 'Board games with close friends',
        priority: 1,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 240,
        dueDate: new Date('2024-12-08'),
        category: 'personal'
      },
      {
        id: 'item_act_2',
        content: 'Plan hiking trip with outdoor group',
        description: 'Half-day hike and lunch',
        priority: 2,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 300,
        dueDate: new Date('2024-12-15'),
        category: 'personal'
      },
      {
        id: 'item_act_3',
        content: 'Book escape room',
        description: 'Team activity with work friends',
        priority: 3,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 120,
        dueDate: new Date('2024-12-22'),
        category: 'personal'
      }
    ]
  },
  // Third list for friend connections goal
  {
    id: 'list_new_connections',
    title: '🤝 New Connections',
    description: 'Expand social circle through new activities',
    status: 'ready',
    priority: 4,
    goalId: 'goal_friend_connections',
    deadline: new Date('2025-06-30'),
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-11-26'),
    estimatedHours: 8,
    items: [
      {
        id: 'item_new_1',
        content: 'Join local running club',
        description: 'Weekly group runs',
        priority: 1,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 90,
        dueDate: new Date('2025-01-15'),
        category: 'personal'
      },
      {
        id: 'item_new_2',
        content: 'Attend tech meetup',
        description: 'Monthly developer community event',
        priority: 2,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 120,
        dueDate: new Date('2025-01-20'),
        category: 'personal'
      },
      {
        id: 'item_new_3',
        content: 'Take photography class',
        description: 'Meet people with similar interests',
        priority: 3,
        createdAt: new Date('2024-11-26'),
        updatedAt: new Date('2024-11-26'),
        estimatedMinutes: 180,
        dueDate: new Date('2025-02-01'),
        category: 'personal'
      }
    ]
  },
  // Second list for portfolio goal
  {
    id: 'list_design_portfolio',
    title: '🎨 Design Projects',
    description: 'UI/UX and graphic design work for portfolio',
    status: 'ready',
    priority: 6,
    goalId: 'goal_portfolio',
    deadline: new Date('2025-08-31'),
    createdAt: new Date('2024-11-15'),
    updatedAt: new Date('2024-11-15'),
    estimatedHours: 40,
    items: [
      {
        id: 'item_design_1',
        content: 'Mobile app UI concept',
        description: 'Fitness tracking app redesign',
        priority: 1,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 480,
        dueDate: new Date('2025-02-01'),
        category: 'creative'
      },
      {
        id: 'item_design_2',
        content: 'Website design case study',
        description: 'Document design process end-to-end',
        priority: 2,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 600,
        dueDate: new Date('2025-03-15'),
        category: 'creative'
      },
      {
        id: 'item_design_3',
        content: 'Brand identity project',
        description: 'Logo, colors, typography system',
        priority: 3,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 720,
        dueDate: new Date('2025-05-01'),
        category: 'creative'
      }
    ]
  },
  // Third list for portfolio goal
  {
    id: 'list_portfolio_marketing',
    title: '📢 Portfolio Promotion',
    description: 'Share and promote creative work',
    status: 'ready',
    priority: 7,
    goalId: 'goal_portfolio',
    deadline: new Date('2025-08-31'),
    createdAt: new Date('2024-11-15'),
    updatedAt: new Date('2024-11-15'),
    estimatedHours: 20,
    items: [
      {
        id: 'item_promo_1',
        content: 'Set up Instagram portfolio account',
        description: 'Curate feed for creative work',
        priority: 1,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 120,
        dueDate: new Date('2025-01-15'),
        category: 'creative'
      },
      {
        id: 'item_promo_2',
        content: 'Create Behance profile',
        description: 'Upload projects with case studies',
        priority: 2,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 180,
        dueDate: new Date('2025-03-01'),
        category: 'creative'
      },
      {
        id: 'item_promo_3',
        content: 'Submit to design showcases',
        description: 'Dribbble, Awwwards submissions',
        priority: 3,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 120,
        dueDate: new Date('2025-06-01'),
        category: 'creative'
      },
      {
        id: 'item_promo_4',
        content: 'Reach out to potential clients',
        description: 'Cold outreach to local businesses',
        priority: 4,
        createdAt: new Date('2024-11-15'),
        updatedAt: new Date('2024-11-15'),
        estimatedMinutes: 240,
        dueDate: new Date('2025-07-01'),
        category: 'creative'
      }
    ]
  }
];